import { useState } from "react";
import { Box, useThemeUI } from "theme-ui";
import { useSelector } from "react-redux";
import getOr from "lodash/fp/getOr";

import {
  displayReviewModal,
  displaySaveModal,
  displayShareModal,
} from "store/app/appSlice";
import {
  selectCustomisationNotifications,
  selectSelectedProduct,
  selectProductCost,
} from "store/app";
import {
  LoadingIndicator,
  MobileMenu,
  Notifications,
  PersonalizationName,
} from "components";
import ActionsBarMobile from "./ActionsBarMobile";
import AddToOrder from "./AddToOrder";
import ProductDetails from "./ProductDetails";
import CustomisationsContainer from "./CustomisationsContainer";
import ProductImageContainer from "./ProductImageContainer";
import * as styles from "./Product.styles";

export default function Product({
  isAddingProductToOrder,
  customisations,
  changeProduct,
  selectedCustomisationId,
  userChoiceValues,
  selectedImageViewAngle,
  imageViewAnglesObj,
  showChangeImageAngleArrowButtons,

  dispatchSetCustomisationId,
  dispatchSetSelectedImageAngle,
  dispatch,
}) {
  const {
    theme: { isMobile, isDesktop },
  } = useThemeUI();

  const products = useSelector(getOr([], ["app", "formData", "products"]));
  const product = useSelector(selectSelectedProduct);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const cost = useSelector(selectProductCost);

  const { playerName, playerNumber, engineeredFor } = useSelector(
    getOr({}, ["app", "userChoiceValues"])
  );

  const customisationNotifications = useSelector(
    selectCustomisationNotifications
  );

  const dispatchDisplayReviewModal = () => dispatch(displayReviewModal());
  const dispatchDisplaySaveModal = () => dispatch(displaySaveModal());
  const dispatchDisplayShareModal = () => dispatch(displayShareModal());

  return (
    <>
      {isAddingProductToOrder && (
        <Box sx={styles.loadingIndicatorContainer}>
          <LoadingIndicator />
        </Box>
      )}

      {isMobileMenuOpen && (
        <MobileMenu
          gameFitDealerLink={product?.gameFitDealerLink}
          dispatchDisplaySaveModal={dispatchDisplaySaveModal}
          dispatchDisplayShareModal={dispatchDisplayShareModal}
          toggleMobileMenu={() => {
            setIsMobileMenuOpen(!isMobileMenuOpen);
          }}
        />
      )}

      <Box sx={styles.gridLayout}>
        <Box sx={styles.productContainer}>
          <Box sx={styles.productDetailsContainer}>
            <ProductDetails
              product={product}
              cost={cost}
              selectedImageViewAngle={selectedImageViewAngle}
              imageViewAnglesObj={imageViewAnglesObj}
              setSelectedImageAngle={dispatchSetSelectedImageAngle}
              showChangeButton={products.length > 1}
              changeProduct={changeProduct}
            />
          </Box>

          {!isMobile && customisationNotifications.length > 0 && (
            <Box sx={styles.notificationsContainer}>
              <Notifications
                notifications={customisationNotifications}
                canClose
              />
            </Box>
          )}

          <ProductImageContainer
            selectedImageViewAngle={selectedImageViewAngle}
            imageViewAnglesObj={imageViewAnglesObj}
            setSelectedImageAngle={dispatchSetSelectedImageAngle}
            showChangeImageAngleArrowButtons={showChangeImageAngleArrowButtons}
          />

          {isDesktop && (
            <AddToOrder
              gameFitDealerLink={product?.gameFitDealerLink}
              dispatchDisplaySaveModal={dispatchDisplaySaveModal}
              dispatchDisplayShareModal={dispatchDisplayShareModal}
            />
          )}

          <Box sx={styles.personalizationNameContainer}>
            <PersonalizationName
              playerName={playerName || (product?.name || "").split(" ")[0]}
              playerNumber={
                playerName
                  ? playerNumber || ""
                  : (product?.name || "").split(" ")[1]
              }
              engineeredFor={engineeredFor || product?.subName || ""}
            />
          </Box>
        </Box>

        <CustomisationsContainer
          customisations={customisations}
          selectedCustomisationId={selectedCustomisationId}
          userChoiceValues={userChoiceValues}
          dispatchSetCustomisationId={dispatchSetCustomisationId}
          dispatchDisplayReviewModal={dispatchDisplayReviewModal}
        />

        {!isDesktop && (
          <Box sx={styles.bottomBarContainer}>
            <ActionsBarMobile
              displayReviewModal={dispatchDisplayReviewModal}
              toggleMobileMenu={() => {
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              selectedImageViewAngle={selectedImageViewAngle}
              imageViewAnglesObj={imageViewAnglesObj}
              setSelectedImageAngle={dispatchSetSelectedImageAngle}
            />
          </Box>
        )}
      </Box>
    </>
  );
}
